import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { chatbotAPI, handleAPIError } from '../services/api';
import { 
  X,
  Play,
  MessageCircle,
  ExternalLink,
  CheckCircle,
  ArrowRight,
  TestTube,
  Sparkles
} from 'lucide-react';

const CustomerTestingModal = ({ chatbot, isOpen, onClose, onUpdate }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const handleReadyForPayment = async () => {
    setLoading(true);
    try {
      await chatbotAPI.updateChatbotToFinalPayment(chatbot.id);
      toast({
        title: "Ready for Payment!",
        description: "Your chatbot testing is complete. Proceeding to final payment and plan selection.",
      });
      onUpdate();
      onClose();
    } catch (error) {
      const errorMessage = handleAPIError(error);
      toast({
        title: "Failed to Continue",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !chatbot) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-gray-900 border-violet-500/20">
        <CardHeader className="border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl text-white flex items-center">
                <TestTube className="w-8 h-8 mr-3 text-blue-400" />
                Test Your Chatbot
              </CardTitle>
              <CardDescription className="text-gray-400 mt-2">
                Your chatbot is ready for testing. Try it out and when you're satisfied, proceed to final payment.
              </CardDescription>
            </div>
            <Button variant="ghost" onClick={onClose} className="text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          {/* Testing Instructions */}
          <div className="mb-6">
            <Card className="bg-blue-900/20 border-blue-500/20">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Play className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      <Sparkles className="w-4 h-4 inline mr-1" />
                      Your Chatbot is Ready for Testing!
                    </h3>
                    <p className="text-blue-300 text-sm mb-3">
                      Test all features and functionality. When you're satisfied with the chatbot's performance, 
                      proceed to complete the final payment and select your subscription plan.
                    </p>
                    <div className="text-blue-300 text-sm">
                      <p className="mb-1">✓ Ask various questions to test responses</p>
                      <p className="mb-1">✓ Try different conversation flows</p>
                      <p className="mb-1">✓ Test the chatbot's knowledge of your business</p>
                      <p>✓ Verify the design and customization</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Chatbot Testing Frame */}
          <div className="mb-6">
            <Card className="bg-gray-800/50 border-violet-500/20">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  <div className="flex items-center">
                    <MessageCircle className="w-5 h-5 mr-2 text-violet-400" />
                    Live Chatbot Test
                  </div>
                  <Badge className="bg-green-600/20 text-green-300 border-green-500/30">
                    <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                    Live
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Interactive preview of your chatbot - test it just like your customers will
                </CardDescription>
              </CardHeader>
              <CardContent>
                {chatbot.fastbots_url ? (
                  <div className="relative">
                    <div className="bg-gray-900 rounded-lg p-4 border border-gray-600 mb-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                          <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-gray-400 text-xs">brixbot.ai</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-blue-400 hover:text-blue-300"
                            onClick={() => window.open(chatbot.fastbots_url, '_blank')}
                          >
                            <ExternalLink className="w-3 h-3 mr-1" />
                            Open in New Tab
                          </Button>
                        </div>
                      </div>
                      
                      {/* Embedded iframe */}
                      <div className="w-full h-96 bg-white rounded overflow-hidden">
                        <iframe
                          src={chatbot.fastbots_url}
                          className="w-full h-full border-0"
                          title="Chatbot Testing Interface"
                          allow="microphone; camera"
                          sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                        />
                      </div>
                    </div>
                    
                    <div className="text-center">
                      <Button
                        variant="outline"
                        onClick={() => window.open(chatbot.fastbots_url, '_blank')}
                        className="border-violet-500/20 text-violet-400 hover:bg-violet-500/10"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Test in Full Screen
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-700 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <MessageCircle className="w-8 h-8 text-gray-500" />
                    </div>
                    <h3 className="text-white font-medium mb-2">Testing URL Not Available</h3>
                    <p className="text-gray-400 text-sm">
                      The testing URL hasn't been configured yet. Please contact support if you need to test your chatbot.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Next Steps */}
          <div className="mb-6">
            <Card className="bg-violet-900/20 border-violet-500/20">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-white mb-3">
                  Ready to go live? 🚀
                </h3>
                <p className="text-violet-300 text-sm mb-4">
                  Once you're satisfied with your chatbot's performance, complete the final payment 
                  to unlock deployment options and start your subscription.
                </p>
                <div className="text-violet-300 text-sm space-y-1">
                  <p>Next steps after payment:</p>
                  <p className="ml-4">1. 💳 Complete $400 final payment</p>
                  <p className="ml-4">2. 📋 Choose your monthly subscription plan</p>
                  <p className="ml-4">3. 🎯 Get deployment codes for your website</p>
                  <p className="ml-4">4. 🌟 Start your 30-day free trial period</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Actions */}
          <div className="flex justify-between">
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={loading}
            >
              Continue Testing Later
            </Button>
            <Button
              onClick={handleReadyForPayment}
              disabled={loading}
              className="bg-violet-600 hover:bg-violet-700"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  I'm Ready for Final Payment
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerTestingModal;